import { Link } from 'react-router-dom'
import { ArrowRight, Wifi, Smartphone, Zap, Clock, Star, Signal, MapPin, Mail, CheckCircle, AlertCircle, Loader2, X, User, Phone } from 'lucide-react'
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSEO } from '@/hooks/useSEO'
import ReCAPTCHA from 'react-google-recaptcha'
import api from '@/services/api'
import SiluxPromise from '@/components/SiluxPromise'

export default function Home() {
  const [coveragePostcode, setCoveragePostcode] = useState('')
  const [newsletterForm, setNewsletterForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
  })
  const [newsletterSuccess, setNewsletterSuccess] = useState<string | null>(null)
  const [newsletterError, setNewsletterError] = useState<string | null>(null)
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false)
  const [newsletterRecaptcha, setNewsletterRecaptcha] = useState<string | null>(null)
  const newsletterRecaptchaRef = useRef<ReCAPTCHA>(null)
  const navigate = useNavigate()

  // Load SEO data from backend
  useSEO('home')

  // The home background (full-viewport tiled image) is applied at the layout
  // level in WebsiteLayout, keyed on the route, so it is set here and cleared on
  // every other page — including prerendered pages and hard refreshes.

  const { data: spinWheelPromo } = useQuery({
    queryKey: ['spin-wheel-public-home'],
    queryFn: async () => {
      const res = await api.get('/spin-wheel/public')
      return res.data
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const newsletterMutation = useMutation({
    mutationFn: async (data: typeof newsletterForm & { recaptcha_token: string | null }) => {
      const response = await api.post('/newsletter/subscribe', data)
      return response.data
    },
    onSuccess: () => {
      setNewsletterSuccess('Thank you for signing up to our newsletter!')
      setNewsletterError(null)
      setNewsletterForm({ first_name: '', last_name: '', email: '', mobile_number: '' })
      setNewsletterRecaptcha(null)
      newsletterRecaptchaRef.current?.reset()
    },
    onError: (err: any) => {
      setNewsletterError(err.response?.data?.detail || 'Failed to subscribe. Please try again.')
      setNewsletterSuccess(null)
      setNewsletterRecaptcha(null)
      newsletterRecaptchaRef.current?.reset()
    },
  })

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setNewsletterSuccess(null)
    setNewsletterError(null)
    newsletterMutation.mutate({ ...newsletterForm, recaptcha_token: newsletterRecaptcha })
  }

  const openNewsletterPopup = () => {
    setNewsletterSuccess(null)
    setNewsletterError(null)
    setNewsletterRecaptcha(null)
    newsletterRecaptchaRef.current?.reset()
    setShowNewsletterPopup(true)
  }

  const handleCoverageCheck = (e: React.FormEvent) => {
    e.preventDefault()
    if (coveragePostcode.trim()) {
      navigate(`/network-coverage?postcode=${encodeURIComponent(coveragePostcode.trim().toUpperCase())}`)
    }
  }

  return (
    <div className="relative">

      {/* Hero Section */}
      <section className="relative pt-12 sm:pt-20 pb-20 sm:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/50 backdrop-blur-sm border border-primary-100 mb-6 sm:mb-8 animate-fade-in-down">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                Trusted by 10,000+ customers
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl xs:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 animate-fade-in-up">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 via-primary-500 to-cyan-600">
                Ultra-Fast Broadband
              </span>
              <br />
              <span className="text-gray-800">
                & Mobile Plans
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg xs:text-xl md:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Experience lightning-fast connectivity with our award-winning networks.
              <span className="block mt-2 text-base xs:text-lg">No hidden fees. No hassle. Just pure speed.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-in-up animation-delay-400">
              <Link
                to="/broadband"
                className="group relative px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-2xl font-semibold shadow-lg shadow-primary-500/50 hover:shadow-xl hover:shadow-primary-500/60 transition-all duration-300 hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-2 justify-center">
                  Explore Broadband
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              <Link
                to="/sim-only"
                className="group px-8 py-4 bg-white/80 backdrop-blur-sm text-gray-900 rounded-2xl font-semibold border-2 border-gray-200 hover:border-primary-300 hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-2 justify-center">
                  View SIM Plans
                  <Smartphone className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </span>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-600 animate-fade-in-up animation-delay-600">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                <span>24-hour chat support</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                <span>4.5/5 Google Reviews</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiluxPromise variant="all" />

      {/* Broadband Selector Guide Banner */}
      <section className="relative py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/broadband/guide"
            className="group relative flex flex-col sm:flex-row items-center gap-5 sm:gap-8 overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-teal-950 via-slate-900 to-emerald-950 text-white px-6 sm:px-10 py-7 sm:py-8 hover:shadow-xl hover:shadow-teal-900/40 transition-shadow border border-teal-900/50"
          >
            <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_75%_50%,#5eead4,transparent_60%)]" />
            <div className="relative flex-shrink-0 w-16 h-16 bg-teal-800/40 rounded-2xl flex items-center justify-center">
              <Wifi className="w-8 h-8 text-teal-300" />
            </div>
            <div className="relative flex-1 text-center sm:text-left">
              <p className="text-xs font-semibold uppercase tracking-widest text-teal-400/70 mb-1">New Feature</p>
              <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Broadband Selector Guide</h2>
              <p className="text-slate-400 text-sm sm:text-base max-w-xl">
                Enter your postcode to check 4G & 5G coverage in your area, then find the
                cheapest broadband package that fits — MiFi, Lite, or Premium.
              </p>
            </div>
            <div className="relative flex-shrink-0 flex items-center gap-2 bg-teal-700/60 hover:bg-teal-700/80 border border-teal-600/50 text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm whitespace-nowrap">
              Find My Package
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl xs:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Silux Mobile?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Experience the difference with our premium services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Feature Card 1 */}
            <div className="group relative overflow-hidden rounded-2xl sm:rounded-3xl p-6 sm:p-8 bg-white/60 backdrop-blur-sm border border-gray-200 hover:border-primary-300 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-primary-400/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
              <div className="relative">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary-500 to-primary-500 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <Wifi className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  Lightning Fast Speeds
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Home broadband over 4G and 5G — 80–400 Mbps on 5G, no landline and no engineer. Stream, game and work without interruption.
                </p>
                <Link
                  to="/broadband"
                  className="inline-flex items-center gap-2 mt-6 text-primary-600 font-semibold group-hover:gap-3 transition-all"
                >
                  Learn more
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Feature Card 2 */}
            <div className="group relative overflow-hidden rounded-2xl sm:rounded-3xl p-6 sm:p-8 bg-white/60 backdrop-blur-sm border border-gray-200 hover:border-cyan-300 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
              <div className="relative">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <Smartphone className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  Flexible SIM Plans
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Choose from contract or Pay & Go options. Unlimited data plans with no speed throttling. Switch anytime.
                </p>
                <Link
                  to="/sim-only"
                  className="inline-flex items-center gap-2 mt-6 text-cyan-600 font-semibold group-hover:gap-3 transition-all"
                >
                  View plans
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Feature Card 3 */}
            <div className="group relative overflow-hidden rounded-2xl sm:rounded-3xl p-6 sm:p-8 bg-white/60 backdrop-blur-sm border border-gray-200 hover:border-pink-300 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
              <div className="relative">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  24-Hour Chat Support
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Chat support is available 24 hours a day, and our UK-based team is here Monday to Friday, 9am to 5pm (closed weekends and bank holidays).
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 mt-6 text-pink-600 font-semibold group-hover:gap-3 transition-all"
                >
                  Contact us
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Network Coverage Banner */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-primary-900 to-primary-800">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5" />
        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Signal className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-medium text-white">Network Coverage Checker</span>
            </div>
            <h2 className="text-3xl xs:text-4xl md:text-5xl font-bold text-white mb-4">
              Check the Coverage in Your Area
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
              Enter your postcode to see 4G and 5G coverage from all UK networks including Three, Vodafone, O2, and EE.
            </p>
          </div>

          <form onSubmit={handleCoverageCheck} className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={coveragePostcode}
                  onChange={(e) => setCoveragePostcode(e.target.value.toUpperCase())}
                  placeholder="Enter your postcode (e.g. SW1A 1AA)"
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all text-lg font-mono"
                  maxLength={10}
                />
              </div>
              <button
                type="submit"
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-bold shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                <Signal className="w-5 h-5" />
                Check Coverage
              </button>
            </div>
          </form>

          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary-400"></div>
              <span>Three</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>Vodafone</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>O2</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-teal-500"></div>
              <span>EE</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-primary-600 via-primary-500 to-pink-600 p-8 sm:p-12 md:p-16 text-center shadow-2xl">
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
            <div className="relative">
              <h2 className="text-3xl xs:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-lg sm:text-xl text-white mb-8 sm:mb-10 max-w-2xl mx-auto">
                Join thousands of satisfied customers and experience the Silux Mobile difference today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-primary-600 rounded-xl sm:rounded-2xl font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  Create Free Account
                </Link>
                <Link
                  to="/broadband"
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl sm:rounded-2xl font-bold border-2 border-white/30 hover:bg-white/20 transition-all"
                >
                  Browse Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spin & Win Promotional Banner */}
      {spinWheelPromo?.visible && (
        <section className="relative py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <Link
              to="/spin-wheel"
              className="group relative flex flex-col sm:flex-row items-center gap-5 sm:gap-8 overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-primary-900 via-gray-900 to-primary-900 text-white px-6 sm:px-10 py-7 sm:py-9 hover:shadow-xl hover:shadow-primary-900/40 transition-shadow border border-primary-800/40"
            >
              <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_70%_50%,#40A345,transparent_60%)]" />

              {/* Icon */}
              <div className="relative flex-shrink-0 w-16 h-16 bg-primary-800/40 rounded-2xl flex items-center justify-center text-3xl select-none">
                🎡
              </div>

              {/* Text */}
              <div className="relative flex-1 text-center sm:text-left">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary-400/80 mb-1">
                  {spinWheelPromo.campaign_name || 'Exclusive Offer'} · Limited Time
                </p>
                <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">
                  {spinWheelPromo.heading || 'Spin to Save!'}
                </h2>
                <p className="text-gray-400 text-sm sm:text-base max-w-xl">
                  {spinWheelPromo.subheading
                    ? spinWheelPromo.subheading
                    : 'Register for free and spin the wheel for an exclusive discount. New customers only — codes valid for 14 days.'}
                </p>
              </div>

              {/* CTA */}
              <div className="relative flex-shrink-0 flex items-center gap-2 bg-white text-primary-700 font-bold px-5 py-2.5 rounded-xl transition-colors text-sm whitespace-nowrap group-hover:bg-primary-50">
                Register &amp; Spin
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>

            <p className="text-center text-[11px] text-gray-400 mt-3">
              One spin per customer. Codes valid 14 days. Excludes top-ups, account payments, and ineligible products.{' '}
              <Link to="/spin-wheel" className="text-primary-600 hover:underline">Full T&amp;Cs</Link>
            </p>
          </div>
        </section>
      )}

      {/* Newsletter Signup Section */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 mb-6">
            <Mail className="w-5 h-5 text-primary-600" />
            <span className="text-sm font-medium text-primary-700">Stay Updated</span>
          </div>
          <h2 className="text-3xl xs:text-4xl font-bold text-gray-900 mb-4">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Get exclusive offers, promotions, and the latest updates delivered straight to your inbox.
          </p>
          <button
            onClick={openNewsletterPopup}
            className="px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto"
          >
            <Mail className="w-5 h-5" />
            Subscribe Now
          </button>
          <p className="text-xs text-gray-500 mt-4">
            No account required. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Sitemap / Quick Links Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Products */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-primary-300">Products</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/sim-only" className="hover:text-primary-300 transition-colors">SIM Only Deals</Link></li>
                <li><Link to="/broadband" className="hover:text-primary-300 transition-colors">Broadband</Link></li>
                <li><Link to="/bundles" className="hover:text-primary-300 transition-colors">Bundles</Link></li>
                <li><Link to="/roaming" className="hover:text-primary-300 transition-colors">Roaming</Link></li>
                {/* <li><Link to="/free-sim" className="hover:text-primary-300 transition-colors">Free SIM</Link></li> */}{/* Temporarily removed - uncomment to re-enable */}
                <li><Link to="/special-offers" className="hover:text-primary-300 transition-colors">Special Offers</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-primary-300">Support</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/faq" className="hover:text-primary-300 transition-colors">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-primary-300 transition-colors">Contact Us</Link></li>
                <li><Link to="/network-coverage" className="hover:text-primary-300 transition-colors">Network Coverage</Link></li>
                <li><Link to="/account/support" className="hover:text-primary-300 transition-colors">Support Tickets</Link></li>
                <li><Link to="/repairs-replacements" className="hover:text-primary-300 transition-colors">Repairs & Replacements</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-primary-300">Company</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/about" className="hover:text-primary-300 transition-colors">About Us</Link></li>
                <li><Link to="/reseller-program" className="hover:text-primary-300 transition-colors">Reseller Program</Link></li>
                <li><Link to="/terms" className="hover:text-primary-300 transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-primary-300 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/acceptable-use" className="hover:text-primary-300 transition-colors">Acceptable Use Policy</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-primary-300 transition-colors">Cookie Policy</Link></li>
                <li><Link to="/website-terms" className="hover:text-primary-300 transition-colors">Website Terms of Use</Link></li>
                <li><Link to="/complaints-code" className="hover:text-primary-300 transition-colors">Complaints Code of Practice</Link></li>
                <li><Link to="/early-termination" className="hover:text-primary-300 transition-colors">Early Termination Charges</Link></li>
              </ul>
            </div>

            {/* Account */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-primary-300">Account</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/login" className="hover:text-primary-300 transition-colors">Login</Link></li>
                <li><Link to="/register" className="hover:text-primary-300 transition-colors">Register</Link></li>
                <li><Link to="/account" className="hover:text-primary-300 transition-colors">My Account</Link></li>
                <li><Link to="/account/orders" className="hover:text-primary-300 transition-colors">My Orders</Link></li>
                <li><button onClick={openNewsletterPopup} className="hover:text-primary-300 transition-colors">Newsletter</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} Silux Mobile. All rights reserved.</p>
            <p className="mt-2">
              {/* Plain <a> (not router Link) so it hits the server and returns the XML sitemap */}
              <a href="/sitemap.xml" className="hover:text-primary-300 transition-colors">Sitemap</a>
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Popup Modal */}
      {showNewsletterPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowNewsletterPopup(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Subscribe to Newsletter</h3>
              <button onClick={() => setShowNewsletterPopup(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {newsletterSuccess ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You for Subscribing!</h3>
                <p className="text-gray-600 mb-6">
                  Be sure to look out for all our specials and exclusive offers in your inbox.
                </p>
                <button
                  onClick={() => setShowNewsletterPopup(false)}
                  className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all"
                >
                  Got it!
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  Subscribe to receive the latest offers, promotions, and updates from Silux Mobile.
                </p>

                {newsletterError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {newsletterError}
                  </div>
                )}

                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={newsletterForm.first_name}
                          onChange={(e) => setNewsletterForm(prev => ({ ...prev, first_name: e.target.value }))}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="John"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={newsletterForm.last_name}
                          onChange={(e) => setNewsletterForm(prev => ({ ...prev, last_name: e.target.value }))}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Doe"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        value={newsletterForm.email}
                        onChange={(e) => setNewsletterForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="john.doe@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        value={newsletterForm.mobile_number}
                        onChange={(e) => setNewsletterForm(prev => ({ ...prev, mobile_number: e.target.value }))}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="07XXX XXXXXX"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Optional - receive SMS notifications about exclusive offers</p>
                  </div>

                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={newsletterRecaptchaRef}
                      sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                      onChange={setNewsletterRecaptcha}
                      onExpired={() => setNewsletterRecaptcha(null)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={newsletterMutation.isPending || !newsletterRecaptcha}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {newsletterMutation.isPending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Subscribing...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Subscribe to Newsletter
                      </>
                    )}
                  </button>
                </form>

                <p className="text-xs text-gray-500 text-center mt-4">
                  By subscribing, you agree to receive marketing communications from Silux Mobile.
                  You can unsubscribe at any time.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(20px, -50px) scale(1.1);
          }
          50% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          75% {
            transform: translate(50px, 50px) scale(1.05);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out forwards;
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  )
}
